import React, { useState, useEffect, useCallback } from 'react';
import { permissionService, botService, authService, settingsService } from '../../services';
import BackButton from '../../components/BackButton';
import './PermissionManagement.css';

function PermissionManagement() {
  const [users, setUsers] = useState([]);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRooms, setUserRooms] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [maxRooms, setMaxRooms] = useState(0);
  const [maxQQGroups, setMaxQQGroups] = useState(0);
  const [showRoomAssignment, setShowRoomAssignment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activatingBotUserId, setActivatingBotUserId] = useState(null);
  const [activationConfirm, setActivationConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await permissionService.getAllUsersWithPermissions(currentPage, 10, searchTerm);
      setUsers(data.users);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      setError('加载用户列表失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const loadPermissionTypes = useCallback(async () => {
    try {
      const types = await permissionService.getPermissionTypes();
      setPermissionTypes(types);
    } catch (err) {
      console.error('加载权限类型失败:', err);
    }
  }, []);

  const loadAvailableRooms = useCallback(async () => {
    try {
      const config = await botService.getBotConfig();
      if (config && config.rooms) {
        const seen = new Set();
        setAvailableRooms(config.rooms.filter(room => {
          const uid = String(room.uid ?? room.anchor_info?.uid ?? '');
          if (!uid || seen.has(uid)) return false;
          seen.add(uid);
          return true;
        }));
      }
    } catch (err) {
      console.error('加载房间列表失败:', err);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await settingsService.getRegistrationStatus();
      setRegistrationOpen(data.registrationOpen);
    } catch (err) {
      console.error('加载设置失败:', err);
    }
  }, []);

  useEffect(() => {
    loadPermissionTypes();
    loadAvailableRooms();
    loadSettings();
  }, [loadPermissionTypes, loadAvailableRooms, loadSettings]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setActivationConfirm(null);
    loadUsers();
  };

  const handlePageChange = (updater) => {
    setActivationConfirm(null);
    setCurrentPage(updater);
  };

  const handleSelectUser = async (user) => {
    setActivationConfirm(null);
    setSelectedUser(user);
    setUserPermissions(user.permissions || []);
    setUserRooms((user.rooms || []).map(String));
    setUserRole(user.role || 'user');
    setMaxRooms(user.max_rooms || 0);
    setMaxQQGroups(user.max_qq_groups || 0);
    setShowRoomAssignment(false);
    setSuccessMessage('');
  };

  const handlePermissionToggle = (permissionKey) => {
    setUserPermissions(prev => {
      if (prev.includes(permissionKey)) {
        return prev.filter(p => p !== permissionKey);
      } else {
        return [...prev, permissionKey];
      }
    });
  };

  const handleRoomToggle = (roomId) => {
    const normalizedRoomId = String(roomId);
    setUserRooms(prev => {
      if (prev.includes(normalizedRoomId)) {
        return prev.filter(r => r !== normalizedRoomId);
      } else {
        return [...prev, normalizedRoomId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    
    try {
      setSaving(true);
      setError('');
      
      // Update role if changed
      if (userRole !== selectedUser.role) {
        await authService.updateUserRole(selectedUser.id, userRole);
      }
      
      // Save permissions (excluding bot.room.view which is now controlled by max_rooms)
      const permissionsToSave = userPermissions.filter(p => p !== 'bot.room.view');
      await permissionService.updateUserPermissions(selectedUser.id, permissionsToSave);
      
      // Save user limits (max rooms and max QQ groups)
      await permissionService.updateUserLimits(selectedUser.id, maxRooms, maxQQGroups);
      
      // Save room assignments if modified
      await permissionService.assignUserRooms(selectedUser.id, userRooms);
      
      setSuccessMessage('权限保存成功！');
      
      // Reload users to get updated data
      loadUsers();
    } catch (err) {
      setError('保存权限失败：' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleBotActivation = async (user) => {
    const bilibiliUid = user.bilibili_uid ? String(user.bilibili_uid) : '';
    if (!bilibiliUid) {
      setError('该用户尚未绑定 B 站账号。');
      setSuccessMessage('');
      return;
    }

    const confirmKey = `${user.id}:${bilibiliUid}`;
    if (activationConfirm !== confirmKey) {
      window.open(`https://space.bilibili.com/${bilibiliUid}`, '_blank', 'noopener,noreferrer');
      setActivationConfirm(confirmKey);
      setError('');
      setSuccessMessage(`已打开 UID ${bilibiliUid} 的 B 站个人空间，确认无误后请再次点击“一键开通 Bot”。`);
      return;
    }

    try {
      setActivatingBotUserId(user.id);
      setError('');
      const result = await permissionService.activateUserBot(user.id, bilibiliUid);
      const activationData = result.data || {};
      const activatedUid = String(activationData.bilibili_uid || bilibiliUid);
      const activatedRooms = userRooms.includes(activatedUid) ? userRooms : [...userRooms, activatedUid];
      const activatedPermissions = userPermissions.includes('bot.room.view')
        ? userPermissions
        : [...userPermissions, 'bot.room.view'];

      setActivationConfirm(null);
      setUserRole(activationData.role || 'premium');
      setMaxRooms(activationData.max_rooms ?? Math.max(maxRooms, 1));
      setMaxQQGroups(activationData.max_qq_groups ?? Math.max(maxQQGroups, 1));
      setUserRooms(activatedRooms);
      setUserPermissions(activatedPermissions);
      setSelectedUser(prev => prev?.id === user.id ? {
        ...prev,
        role: activationData.role || 'premium',
        max_rooms: activationData.max_rooms ?? Math.max(prev.max_rooms || 0, 1),
        max_qq_groups: activationData.max_qq_groups ?? Math.max(prev.max_qq_groups || 0, 1),
        permissions: activatedPermissions,
        rooms: activatedRooms
      } : prev);
      setSuccessMessage(`已为 ${user.username} 开通 Bot 权限。主播 UID：${activatedUid}`);
      await loadUsers();
    } catch (err) {
      if (err.response?.data?.code === 'BINDING_CHANGED') {
        setActivationConfirm(null);
      }
      setError('开通 Bot 权限失败：' + (err.response?.data?.message || err.message));
      setSuccessMessage('');
    } finally {
      setActivatingBotUserId(null);
    }
  };

  const toggleRegistration = async () => {
    try {
      const newState = !registrationOpen;
      await settingsService.updateRegistrationStatus(newState);
      setRegistrationOpen(newState);
    } catch (err) {
      setError('更新注册状态失败');
    }
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setUserPermissions([]);
    setUserRooms([]);
    setUserRole('user');
    setMaxRooms(0);
    setMaxQQGroups(0);
    setShowRoomAssignment(false);
    setActivationConfirm(null);
    setSuccessMessage('');
    setError('');
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return '管理员';
      case 'premium': return '主播';
      default: return '普通用户';
    }
  };

  const getPermissionLabel = (permissions) => {
    if (!permissions || permissions.length === 0) return '无权限';
    return `${permissions.length} 个权限`;
  };

  const selectedBilibiliUid = selectedUser?.bilibili_uid ? String(selectedUser.bilibili_uid) : '';
  const selectedConfirmKey = selectedUser && selectedBilibiliUid ? `${selectedUser.id}:${selectedBilibiliUid}` : null;
  const selectedHasBotAccess = Boolean(selectedUser && (
    selectedUser.role === 'admin' ||
    (selectedUser.max_rooms || 0) > 0 ||
    (selectedUser.permissions || []).includes('bot.room.view')
  ));

  return (
    <div className="permission-management">
      <BackButton to="/" />
      <div className="page-header">
        <h1>🔐 权限管理</h1>
        <div className="registration-toggle">
          <span>注册状态：</span>
          <button 
            className={`toggle-btn ${registrationOpen ? 'open' : 'closed'}`}
            onClick={toggleRegistration}
          >
            {registrationOpen ? '开放' : '关闭'}
          </button>
        </div>
      </div>
      
      <div className="search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="搜索用户名或邮箱..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">搜索</button>
        </form>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && !selectedUser && <div className="success-message">{successMessage}</div>}

      {loading ? (
        <div className="loading">加载中...</div>
      ) : (
        <>
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>用户名</th>
                  <th>邮箱</th>
                  <th>角色</th>
                  <th>权限</th>
                  <th>房间限制</th>
                  <th>QQ限制</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={user.role === 'admin' ? 'admin-row' : ''}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.role === 'admin' ? (
                        <span className="all-permissions">全部权限</span>
                      ) : (
                        <span className="permission-count">
                          {getPermissionLabel(user.permissions)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`limit-badge ${user.max_rooms > 0 ? 'active' : ''}`}>
                        {user.role === 'admin' ? '无限' : `${user.rooms?.length || 0}/${user.max_rooms || 0}`}
                      </span>
                    </td>
                    <td>
                      <span className={`limit-badge ${user.max_qq_groups > 0 ? 'active' : ''}`}>
                        {user.role === 'admin' ? '无限' : (user.max_qq_groups || 0)}
                      </span>
                    </td>
                    <td>
                      {user.role !== 'admin' && (
                        <button
                          className="edit-btn"
                          onClick={() => handleSelectUser(user)}
                        >
                          编辑权限
                        </button>
                      )}
                      {user.role === 'admin' && (
                        <span className="admin-note">管理员无需配置</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(p => p - 1)}
            >
              上一页
            </button>
            <span>第 {currentPage} / {totalPages} 页</span>
            <button 
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(p => p + 1)}
            >
              下一页
            </button>
          </div>
        </>
      )}

      {/* Permission Edit Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="permission-modal" onClick={(e) => e.stopPropagation()}>
            <h2>编辑用户权限</h2>
            <div className="user-info">
              <strong>{selectedUser.username}</strong>
              <span>{selectedUser.email}</span>
            </div>

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
            {error && <div className="error-message">{error}</div>}

            <div className="role-section">
              <h3>用户角色</h3>
              <div className="role-selector">
                <label className={`role-option ${userRole === 'user' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="user"
                    checked={userRole === 'user'}
                    onChange={() => setUserRole('user')}
                  />
                  <span className="role-label">普通用户</span>
                </label>
                <label className={`role-option ${userRole === 'premium' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="premium"
                    checked={userRole === 'premium'}
                    onChange={() => setUserRole('premium')}
                  />
                  <span className="role-label">主播</span>
                </label>
              </div>
            </div>

            <div className="bot-activation-section">
              <h3>B站绑定与 Bot 开通</h3>
              <div className="bot-activation-panel">
                <div className="bot-binding-summary">
                  <div className="bot-binding-info">
                    <span className="bot-binding-label">主绑定账号</span>
                    {selectedUser.has_bilibili_binding ? (
                      <>
                        <strong>{selectedUser.bilibili_uname || '已绑定账号'}</strong>
                        <span className="bot-binding-detail">UID {selectedBilibiliUid}</span>
                      </>
                    ) : (
                      <strong className="muted">未绑定 B 站账号</strong>
                    )}
                  </div>
                  <span className={`bot-status-pill ${selectedHasBotAccess ? 'active' : 'inactive'}`}>
                    {selectedHasBotAccess ? 'Bot 已开通' : 'Bot 未开通'}
                  </span>
                </div>
                <div className="bot-activation-actions">
                  <button
                    type="button"
                    className="activate-bot-btn"
                    onClick={() => handleBotActivation(selectedUser)}
                    disabled={!selectedUser.has_bilibili_binding || activatingBotUserId === selectedUser.id}
                    title={!selectedUser.has_bilibili_binding ? '请先绑定 B 站账号' : '首次点击打开 B 站空间，确认后再次点击开通'}
                  >
                    {activatingBotUserId === selectedUser.id
                      ? '开通中...'
                      : activationConfirm === selectedConfirmKey
                        ? '确认开通'
                        : selectedHasBotAccess
                          ? '重新确认授权'
                          : '一键开通 Bot'}
                  </button>
                  <span className="bot-action-hint">
                    {selectedUser.has_bilibili_binding
                      ? '首次点击会打开 B 站空间，确认后再次点击执行开通。'
                      : '需要用户先完成 B 站绑定。'}
                  </span>
                </div>
              </div>
            </div>

            <div className="permissions-section">
              <h3>功能权限</h3>
              <div className="permissions-grid">
                {permissionTypes
                  .filter(perm => perm.key !== 'bot.room.view') // 不显示 bot.room.view，由 max_rooms 控制
                  .map(perm => (
                  <label key={perm.key} className="permission-item">
                    <input
                      type="checkbox"
                      checked={userPermissions.includes(perm.key)}
                      onChange={() => handlePermissionToggle(perm.key)}
                    />
                    <div className="permission-info">
                      <span className="permission-name">{perm.name}</span>
                      <span className="permission-desc">{perm.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="limits-section">
              <h3>Bot 房间权限</h3>
              <p className="hint">设置用户可配置的房间数和QQ群数。设为0表示无权限。</p>
              
              <div className="limits-grid">
                <div className="limit-input-group">
                  <label>最大房间数</label>
                  <div className="limit-input-wrapper">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={maxRooms}
                      onChange={(e) => setMaxRooms(Math.max(0, parseInt(e.target.value) || 0))}
                    />
                    <span className="limit-hint">当前已分配: {userRooms.length} 个</span>
                  </div>
                </div>
                
                <div className="limit-input-group">
                  <label>最大QQ群数</label>
                  <div className="limit-input-wrapper">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={maxQQGroups}
                      onChange={(e) => setMaxQQGroups(Math.max(0, parseInt(e.target.value) || 0))}
                    />
                    <span className="limit-hint">每个房间可推送的QQ群数量</span>
                  </div>
                </div>
              </div>

              {/* 手动分配房间的折叠区域 */}
              {maxRooms > 0 && (
                <div className="room-assignment-toggle">
                  <button 
                    type="button"
                    className="toggle-room-btn"
                    onClick={() => setShowRoomAssignment(!showRoomAssignment)}
                  >
                    {showRoomAssignment ? '▼' : '▶'} 手动分配已有房间（可选）
                  </button>
                  <span className="optional-hint">
                    用户也可以自行在 Bot 配置页面添加房间
                  </span>
                </div>
              )}
            </div>

            {/* 展开的房间分配区域 */}
            {showRoomAssignment && maxRooms > 0 && (
              <div className="rooms-section">
                <h3>手动分配房间</h3>
                <p className="hint">
                  可以预先为用户分配已有的房间。用户最多可管理 {maxRooms} 个房间，
                  当前已分配 {userRooms.length} 个。
                </p>
                <div className="rooms-grid">
                  {availableRooms.length === 0 ? (
                    <p className="no-rooms">Bot 未连接或无可用房间</p>
                  ) : (
                    availableRooms.map(room => {
                      // 使用 uid 作为唯一标识符（与 BotConfig 和数据库保持一致）
                      const uid = String(room.uid ?? room.anchor_info?.uid ?? '');
                      const roomName = room.anchor_info?.uname || `UID ${uid}`;
                      const roomNumber = room.anchor_info?.room_id;
                      return (
                        <label key={uid} className="room-item">
                          <input
                            type="checkbox"
                            checked={userRooms.includes(uid)}
                            onChange={() => handleRoomToggle(uid)}
                            disabled={!userRooms.includes(uid) && userRooms.length >= maxRooms}
                          />
                          <div className="room-info">
                            <span className="room-name">{roomName}</span>
                            <span className="room-id">
                              UID: {uid}{roomNumber ? ` | 房间号: ${roomNumber}` : ''}
                            </span>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCloseModal}
              >
                取消
              </button>
              <button 
                className="save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存权限'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PermissionManagement;
